/* eslint-disable @next/next/no-img-element */
import { AnimatePresence, motion } from "framer-motion";
import { useRouter } from "next/router";
import { createContext, useEffect, useState } from "react";
import useMediaQuery from "../../hooks/useMediaQuery";
import { Button } from "../Button";
import { LinkWrapper } from "../linkWrapper";
import { Logo } from "../Logo";
import { Background } from "./Background";
import { createStappenplanUrl } from "./createStappenplanUrl";
import { processAction } from "./processAction";
import { ProgressBar } from "./ProgressBar";
import { QuestionComponent } from "./Question";
import { questions } from "./questions";
import { RoundedButton } from "./RoundedButton";
import styles from "./Wizard.module.css";
import { Answers, KnowingType, Questions } from "./wizard.types";

export const ThemeContext = createContext(0);

export const Wizard = () => {
  const initialStep = "2";
  const router = useRouter();
  const matches = useMediaQuery("(min-width: 768px)");
  const [data, setData] = useState<Answers>({} as Answers);
  const [currentStep, setCurrentStep] = useState<keyof Questions>(initialStep);
  const [history, setHistory] = useState<(keyof Questions)[]>([initialStep]);
  const [started, setStarted] = useState<boolean>(false);
  const [direction, setDirection] = useState<number>(() => 0);
  const [number, setNumber] = useState<number>(0);
  const [showStartImage, setShowStartImage] = useState<boolean>(true);

  useEffect(() => {
    const index = Object.keys(questions).findIndex(
      (key) => key === currentStep
    );

    const blueSteps = ["3.1.1", "3.2.1", "4.1", "5"];
    const darkBlueSteps = ["2", "3.1.3.1", "3.1.3.2", "4.3"];
    const greenSteps = ["3.1.2", "4.2", "4.4"];

    if (blueSteps.includes(currentStep)) {
      setNumber(0);
    } else if (darkBlueSteps.includes(currentStep)) {
      setNumber(1);
    } else if (greenSteps.includes(currentStep)) {
      setNumber(2);
    } else setNumber((index - 1) % 3);
  }, [currentStep]);

  const finalize = () => {
    setData((d) => {
      setTimeout(() => {
        router.replace(createStappenplanUrl(d));
      }, 4200);
      return d;
    });
  };

  const setStep = async (value: any) => {
    setDirection(() => 1);
    setHistory((prev) => [...prev, currentStep]);
    processAction(value, currentStep, setCurrentStep, setData, finalize);
  };

  const backClicked = async (value: any) => {
    setDirection(() => -1);

    setCurrentStep((prev) => {
      return prev === initialStep ? prev : (history.at(-1) as keyof Questions);
    });
    setHistory((prev) => {
      return prev.filter((his) => his !== history.at(-1));
    });
  };

  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 300 : -300,
      opacity: 0,
    }),
  };

  const getProgress = () => {
    const keys = Object.keys(questions);
    const index =
      Object.keys(questions).findIndex((v) => v === currentStep) + 1;
    const know = data["2"] === KnowingType.Know;
    const total = know ? keys.length - 15 : keys.length;
    const current = !know ? index : index > 15 ? index - 15 : index;

    const prgrss = Math.round((current / total) * 100);

    return prgrss;
  };

  return (
    <ThemeContext.Provider value={number}>
      <div className={styles.wizardContainer}>
        <motion.div
          initial={showStartImage ? { opacity: 0 } : {}}
          animate={showStartImage ? { opacity: 1 } : {}}
          transition={{ delay: 0.8, duration: 0.35, ease: "easeOut" }}
        >
          {currentStep !== "5" && (
            <RoundedButton
              icon="cross"
              onClick={() => router.push("/")}
              variant={matches && !started ? "light-grey" : "default"}
              crossVariant={
                matches ? "whiteCross" : started ? "whiteCross" : "default"
              }
            />
          )}
        </motion.div>
        <AnimatePresence mode="popLayout">
          {started ? (
            <motion.div
              key="questions"
              initial={{ x: "100%" }}
              animate={{ x: "0%" }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.35, ease: "easeOut" }}
            >
              <Background />
              <ProgressBar progress={getProgress} color={"#FDC133"} />
              <div className={styles.questionsContainer}>
                {currentStep !== "5" && (
                  <RoundedButton
                    icon="chevron-back"
                    variant="default"
                    crossVariant="whiteCross"
                    onClick={
                      currentStep !== initialStep
                        ? backClicked
                        : () => setStarted(false)
                    }
                  />
                )}
                {currentStep === "5" ? (
                  <div className={styles.done}>
                    <div className={styles.container}>
                      <div className={styles.arrow}>
                        <img src="/decorations/arrow-corner.svg" alt="arrow" />
                      </div>
                      <div className={styles.shine}>
                        <img src="/decorations/shine.svg" alt="shine" />
                      </div>
                      <span>Resultaten laden...</span>
                      <div className={styles.underline}>
                        <img
                          src="/decorations/short-line-yellow.svg"
                          alt="underline"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <AnimatePresence
                    initial={false}
                    custom={direction}
                    mode="wait"
                  >
                    <motion.div
                      key={currentStep}
                      custom={direction}
                      variants={variants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{
                        transition: { duration: 0.3, ease: "easeOut" },
                      }}
                    >
                      <QuestionComponent
                        questions={questions}
                        data={data}
                        currentStep={currentStep}
                        setStep={setStep}
                        number={number}
                      />
                    </motion.div>
                  </AnimatePresence>
                )}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="intro"
              initial={{ x: "-100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "-100%" }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              className={styles.intro}
            >
              <LinkWrapper url="homepage">
                <motion.div
                  initial={showStartImage ? { opacity: 0 } : {}}
                  animate={showStartImage ? { opacity: 1 } : {}}
                  transition={{ delay: 1.2, duration: 0.35 }}
                >
                  <Logo className={styles.logo} />
                </motion.div>
              </LinkWrapper>
              {showStartImage ? (
                <motion.div
                  key="image"
                  initial={{
                    width: "100%",
                    height: "100%",
                  }}
                  animate={{
                    [matches ? "width" : "height"]: "50%",
                  }}
                  exit={{ [matches ? "width" : "height"]: "50%" }}
                  transition={{ delay: 1, duration: 0.35 }}
                  className={styles.imagePanel}
                ></motion.div>
              ) : (
                <div className={styles.imagePanel}></div>
              )}
              <div className={styles.ctaPanel}>
                <div className={styles.content}>
                  <h1>Maak jouw plan voor een volgende stap</h1>
                  <p>
                    We stellen jou een aantal vragen en op basis van die
                    antwoorden krijg je een persoonlijk stappenplan.
                  </p>
                  <div className={styles.button}>
                    <Button
                      variation="primary-diap"
                      onClick={() => {
                        setShowStartImage(false);
                        setStarted(true);
                      }}
                    >
                      Start de test
                    </Button>
                    <img src="/decorations/shine.svg" alt="shine" />
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </ThemeContext.Provider>
  );
};
